import React, { useState, useEffect } from "react";
import { Sidebar, Menu, sidebarClasses } from "react-pro-sidebar";
import { tokens } from "../../../theme";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EmailIcon from "@mui/icons-material/Email";
import FunctionsIcon from "@mui/icons-material/Functions";
import FolderIcon from "@mui/icons-material/Folder";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import DeleteIcon from "@mui/icons-material/Delete";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { Box, IconButton, Typography, useTheme, Tooltip, Avatar } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

export default function SuperAdminSidebar({ isCollapsed, setIsCollapsed }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState("Dashboard");
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const user = localStorage.getItem("superAdminUser");
    if (user) {
      setUserInfo(JSON.parse(user));
    }
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("dashboard")) setSelected("Dashboard");
    else if (path.includes("plans") || path.includes("billing")) setSelected("Plans & add-ons");
    else if (path.includes("tenants")) setSelected("Tenants");
    else if (path.includes("doctors")) setSelected("Doctors");
    else if (path.includes("patients")) setSelected("Patients");
    else if (path.includes("forms")) setSelected("Forms");
    else if (path.includes("form-answers")) setSelected("Form Answers");
    else if (path.includes("form-invitations")) setSelected("Form Invitations");
    else if (path.includes("scoring-formulas")) setSelected("Scoring Formulas");
    else if (path.includes("files")) setSelected("Files");
    else if (path.includes("doctor-invitations")) setSelected("Doctor Invitations");
    else if (path.includes("password-resets")) setSelected("Password Resets");
    else if (path.includes("trash-logs")) setSelected("Trash Logs");
  }, [location]);

  const menuItems = [
    {
      title: "Dashboard",
      icon: <DashboardOutlinedIcon />,
      path: "/super-admin/dashboard",
    },
    {
      title: "Plans & add-ons",
      icon: <WorkspacePremiumIcon />,
      path: "/super-admin/plans",
    },
    {
      title: "Tenants",
      icon: <BusinessIcon />,
      path: "/super-admin/tenants",
    },
    {
      title: "Doctors",
      icon: <PeopleIcon />,
      path: "/super-admin/doctors",
    },
    {
      title: "Patients",
      icon: <PersonIcon />,
      path: "/super-admin/patients",
    },
    {
      title: "Forms",
      icon: <DescriptionIcon />,
      path: "/super-admin/forms",
    },
    {
      title: "Form Answers",
      icon: <AssignmentIcon />,
      path: "/super-admin/form-answers",
    },
    {
      title: "Form Invitations",
      icon: <EmailIcon />,
      path: "/super-admin/form-invitations",
    },
    {
      title: "Scoring Formulas",
      icon: <FunctionsIcon />,
      path: "/super-admin/scoring-formulas",
    },
    {
      title: "Files",
      icon: <FolderIcon />,
      path: "/super-admin/files",
    },
    {
      title: "Doctor Invitations",
      icon: <EmailIcon />,
      path: "/super-admin/doctor-invitations",
    },
    {
      title: "Password Resets",
      icon: <VpnKeyIcon />,
      path: "/super-admin/password-resets",
    },
    {
      title: "Trash Logs",
      icon: <DeleteIcon />,
      path: "/super-admin/trash-logs",
    },
  ];

  const handleMenuItemClick = (title, path) => {
    setSelected(title);
    navigate(path);
  };

  return (
    <Box
      sx={{
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Sidebar
        rootStyles={{
          [`.${sidebarClasses.container}`]: {
            backgroundColor: colors.primary[400],
            backgroundImage: `linear-gradient(180deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            height: "100vh",
            borderRight: `1px solid ${colors.grey[800]}30`,
            boxShadow: `4px 0 24px rgba(0, 0, 0, 0.15)`,
            display: "flex",
            flexDirection: "column",
          },
        }}
        collapsed={isCollapsed}
        width="280px"
        collapsedWidth="80px"
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
              borderRadius: "10px",
              margin: "8px 0",
            },
            "&::-webkit-scrollbar-thumb": {
              background: `linear-gradient(180deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
              borderRadius: "10px",
              border: `2px solid ${colors.primary[400]}`,
              transition: "all 0.3s ease",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: `linear-gradient(180deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
              boxShadow: `0 0 8px ${colors.blueAccent[500]}60`,
            },
          }}
        >
          <Menu
            menuItemStyles={{
              button: {
                "&:hover": {
                  backgroundColor: `${colors.blueAccent[800]}20`,
                  transform: "translateX(4px)",
                },
                transition: "all 0.3s ease",
              },
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: isCollapsed ? "center" : "space-between",
                p: isCollapsed ? 2 : 3,
                mb: 2,
                borderBottom: `1px solid ${colors.grey[800]}30`,
                background: `linear-gradient(135deg, ${colors.blueAccent[800]}15 0%, transparent 100%)`,
              }}
            >
              {!isCollapsed && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 4px 12px ${colors.blueAccent[500]}40`,
                    }}
                  >
                    <AdminPanelSettingsIcon sx={{ color: "white", fontSize: "22px" }} />
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      letterSpacing: "0.5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    Super Admin
                  </Typography>
                </Box>
              )}

              <Tooltip title={isCollapsed ? "Expand" : "Collapse"} placement="right">
                <IconButton
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: `${colors.blueAccent[700]}30`,
                    border: `1px solid ${colors.blueAccent[500]}40`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: `${colors.blueAccent[600]}50`,
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  {isCollapsed ? (
                    <ChevronRightIcon sx={{ color: colors.blueAccent[300] }} />
                  ) : (
                    <ChevronLeftIcon sx={{ color: colors.blueAccent[300] }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            {/* User Profile */}
            {!isCollapsed && userInfo && (
              <Box
                sx={{
                  p: 2,
                  m: 2,
                  mb: 2,
                  borderRadius: "12px",
                  background: `linear-gradient(135deg, ${colors.blueAccent[800]}30 0%, ${colors.greenAccent[800]}20 100%)`,
                  border: `1px solid ${colors.blueAccent[700]}40`,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                  }}
                >
                  <AdminPanelSettingsIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: colors.grey[100],
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {userInfo.email || "Super Admin"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.grey[400],
                      fontSize: "0.7rem",
                    }}
                  >
                    Administrator
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Menu Items */}
            {menuItems.map((item) => (
              <Box
                key={item.title}
                onClick={() => handleMenuItemClick(item.title, item.path)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: isCollapsed ? 2 : 3,
                  py: 1.5,
                  mx: 1,
                  mb: 0.5,
                  borderRadius: "12px",
                  cursor: "pointer",
                  backgroundColor:
                    selected === item.title
                      ? `${colors.blueAccent[700]}40`
                      : "transparent",
                  border: `1px solid ${
                    selected === item.title
                      ? colors.blueAccent[500]
                      : "transparent"
                  }`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: `${colors.blueAccent[800]}20`,
                    transform: "translateX(4px)",
                  },
                }}
              >
                <Box
                  sx={{
                    color:
                      selected === item.title
                        ? colors.blueAccent[300]
                        : colors.grey[400],
                    display: "flex",
                    alignItems: "center",
                    minWidth: isCollapsed ? "auto" : "40px",
                  }}
                >
                  {item.icon}
                </Box>
                {!isCollapsed && (
                  <Typography
                    variant="body2"
                    sx={{
                      ml: 2,
                      fontWeight: selected === item.title ? 600 : 400,
                      color:
                        selected === item.title
                          ? colors.grey[100]
                          : colors.grey[300],
                    }}
                  >
                    {item.title}
                  </Typography>
                )}
              </Box>
            ))}
          </Menu>
        </Box>

        {/* Footer */}
        {!isCollapsed && (
          <Box
            sx={{
              p: 2,
              m: 2,
              mt: "auto",
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${colors.blueAccent[800]}30 0%, ${colors.greenAccent[800]}20 100%)`,
              border: `1px solid ${colors.blueAccent[700]}40`,
              backdropFilter: "blur(10px)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.grey[400],
                fontSize: "0.7rem",
                display: "block",
                textAlign: "center",
              }}
            >
              Super Admin Panel v1.0
            </Typography>
          </Box>
        )}
      </Sidebar>
    </Box>
  );
}

